/**
 * Lightweight reactive pub/sub store.
 * Subscribers notified only when their selected slice changes (shallow compare).
 */

type Listener<T, S> = {
  selector: (state: T) => S;
  callback: (slice: S) => void;
  lastValue: S;
};

export interface Store<T> {
  getState(): T;
  setState(partial: Partial<T>): void;
  subscribe<S>(selector: (state: T) => S, callback: (slice: S) => void): () => void;
  destroy(): void;
}

function shallowEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;

  const keysA = Object.keys(a as Record<string, unknown>);
  const keysB = Object.keys(b as Record<string, unknown>);
  if (keysA.length !== keysB.length) return false;

  const objA = a as Record<string, unknown>;
  const objB = b as Record<string, unknown>;
  for (const key of keysA) {
    if (!Object.is(objA[key], objB[key])) return false;
  }
  return true;
}

export function createStore<T extends object>(initialState: T): Store<T> {
  let state = { ...initialState };
  // Using unknown[] and casting at notification time to avoid complex generic tracking
  const listeners: Listener<T, unknown>[] = [];
  let destroyed = false;

  function getState(): T {
    return state;
  }

  function setState(partial: Partial<T>): void {
    if (destroyed) return;
    const next = { ...state, ...partial };
    state = next;
    notifyListeners();
  }

  function notifyListeners(): void {
    for (const listener of listeners) {
      const newValue = listener.selector(state);
      if (!shallowEqual(newValue, listener.lastValue)) {
        listener.lastValue = newValue;
        listener.callback(newValue);
      }
    }
  }

  function subscribe<S>(selector: (state: T) => S, callback: (slice: S) => void): () => void {
    const listener: Listener<T, unknown> = {
      selector: selector as (state: T) => unknown,
      callback: callback as (slice: unknown) => void,
      lastValue: selector(state),
    };
    listeners.push(listener);

    return () => {
      const idx = listeners.indexOf(listener);
      if (idx !== -1) listeners.splice(idx, 1);
    };
  }

  function destroy(): void {
    destroyed = true;
    listeners.length = 0;
  }

  return { getState, setState, subscribe, destroy };
}

// --- Application state shape ---

import type { NormalizedEvent } from '@/sources/base-source';
import type { DeploymentConfig } from '@/config/deployment';

export type TimeRange = '1h' | '6h' | '24h' | '48h' | '7d' | 'all';

export interface AppState {
  variant: string;
  activeLayers: string[];
  viewport: {
    center: [number, number];
    zoom: number;
    bearing: number;
    pitch: number;
    viewMode: 'globe' | 'flat';
  };
  events: NormalizedEvent[];
  panels: {
    left: boolean;
    right: boolean;
    bottom: boolean;
    activePanel: string | null;
  };
  theme: 'dark' | 'light';
  deployment: DeploymentConfig;
  timeRange: TimeRange;
  /** When set, events after this timestamp are hidden (time slider scrubbing). null = live. */
  timeCursor: number | null;
}

export const DEFAULT_APP_STATE: AppState = {
  variant: 'general',
  activeLayers: [],
  viewport: {
    center: [0, 20],
    zoom: 2,
    bearing: 0,
    pitch: 0,
    viewMode: 'flat',
  },
  events: [],
  panels: {
    left: true,
    right: false,
    bottom: true,
    activePanel: null,
  },
  theme: 'dark',
  deployment: { mode: 'online' },
  timeRange: '24h',
  timeCursor: null,
};

/** Singleton application store */
export const appStore = createStore<AppState>(DEFAULT_APP_STATE);
